import { useCallback } from 'react'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { convertDatesInObject } from '@/helpers/api'
import { SweetspotError } from '@/helpers/error'
import {
  type UploadUrlOut,
  computeSha256Hex,
  putToS3,
  retryWithBackoff,
} from '@/helpers/uploadUtils'

import { useHandleError } from '@/hooks/common/useErrorHandler'
import { fileQueryKeys } from '@/hooks/files/types'
import { organizationFilesQueryKeys } from '@/hooks/organization/types'
import { pursuitFilesQueryKeys } from '@/hooks/pursuits/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { File as SweetFile } from '@/types/File'
import type {
  FileVersionDetail,
  FileVersionList,
  WorkingCopyCheckpoint,
  WorkingCopyCheckpointOutcome,
} from '@/types/file/FileVersion'
import type { UUID } from '@/types/Id'

const authorization = (accessToken: string): Record<string, string> => ({
  Authorization: `Bearer ${accessToken}`,
})

const refused = (res: Response): SweetspotError =>
  new SweetspotError({
    name: 'FETCH_ERROR',
    message: res.statusText,
    cause: { status: res.status },
  })

const versionsUrl = (fileId: string): string =>
  `${API_URL}/api/v1/files/${fileId}/versions`

export const fetchFileVersions = async (
  fileId: string,
  accessToken: string,
): Promise<FileVersionList> => {
  const res = await fetch(versionsUrl(fileId), {
    method: 'GET',
    headers: authorization(accessToken),
  })
  if (!res.ok) {
    throw refused(res)
  }
  return (await res.json()) as FileVersionList
}

/** One version with links to its bytes: presigned, so minted per request rather than kept with the list. */
export const fetchFileVersion = async (
  fileId: string,
  versionId: string,
  accessToken: string,
): Promise<FileVersionDetail> => {
  const res = await fetch(`${versionsUrl(fileId)}/${versionId}`, {
    method: 'GET',
    headers: authorization(accessToken),
  })
  if (!res.ok) {
    throw refused(res)
  }
  return (await res.json()) as FileVersionDetail
}

const restoreFileVersion = async (
  fileId: string,
  versionId: string,
  accessToken: string,
): Promise<SweetFile> => {
  const res = await fetch(`${versionsUrl(fileId)}/${versionId}/restore`, {
    method: 'POST',
    headers: authorization(accessToken),
  })
  if (!res.ok) {
    throw refused(res)
  }
  return convertDatesInObject(await res.json())
}

const fetchVersionUploadUrl = async (
  fileId: string,
  size: number,
  contentSha256: string | null,
  accessToken: string,
): Promise<UploadUrlOut> => {
  const url = new URL(`${API_URL}/api/v1/files/${fileId}/content/upload-url`)
  url.searchParams.set('size', String(size))
  // Signed into the URL the same way, so S3 enforces it at PUT time and the
  // confirm step can trust the digest instead of re-reading the object for it.
  if (contentSha256) {
    url.searchParams.set('content_sha256', contentSha256)
  }
  const res = await fetch(url, {
    method: 'GET',
    headers: authorization(accessToken),
  })
  if (!res.ok) {
    throw refused(res)
  }
  return (await res.json()) as UploadUrlOut
}

const confirmFileVersion = async (
  fileId: string,
  s3Key: string,
  accessToken: string,
): Promise<SweetFile> => {
  const res = await fetch(versionsUrl(fileId), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authorization(accessToken),
    },
    body: JSON.stringify({ s3_key: s3Key }),
  })
  if (!res.ok) {
    throw refused(res)
  }
  return convertDatesInObject(await res.json())
}

/** Saves what the editor holds as the next version, if it changed. Null where the server refuses. */
export const checkpointWorkingCopy = async (
  fileId: string,
  accessToken: string,
): Promise<WorkingCopyCheckpoint | null> => {
  const res = await fetch(
    `${API_URL}/api/v1/files/${fileId}/working-copy/checkpoint`,
    {
      method: 'POST',
      headers: authorization(accessToken),
    },
  )
  if (res.status === 403 || res.status === 404) {
    return null
  }
  if (!res.ok) {
    throw refused(res)
  }
  return (await res.json()) as WorkingCopyCheckpoint
}

export const STILL_SAVING_MESSAGE =
  'Edits made in the editor are still being saved. Try again in a moment.'

/**
 * The save before a read, for a caller that would rather read what is there than
 * fail: it re-reads after a save and holds off while one is still running.
 */
export const saveEditorEditsFirst = async (
  fileId: string,
  accessToken: string | null | undefined,
): Promise<WorkingCopyCheckpointOutcome | null> => {
  if (!accessToken) {
    return null
  }
  try {
    const checkpoint = await checkpointWorkingCopy(fileId, accessToken)
    return checkpoint?.outcome ?? null
  } catch {
    return null
  }
}

interface UseFileVersionsArgs<TResult> {
  fileId: UUID | ''
  enabled?: boolean
  select?: (versions: FileVersionList) => TResult
}

export const useFileVersions = <TResult = FileVersionList>({
  fileId,
  enabled = true,
  select,
}: UseFileVersionsArgs<TResult>) => {
  const { accessToken } = useAuthInfoWithThrow()

  return useQuery({
    queryKey: fileQueryKeys.versions({ fileId }),
    queryFn: () => fetchFileVersions(fileId, accessToken),
    enabled: enabled && fileId !== '',
    select,
  })
}

interface UseFileVersionDetailArgs {
  fileId: UUID | ''
  /** Empty while no version is being looked at, which is what turns this off. */
  versionId: UUID | ''
}

/**
 * One version, with links to its bytes. Presigned and expiring, so the refetch on
 * mount and on focus is what keeps a viewer left open on one off a dead URL.
 */
export const useFileVersionDetail = ({
  fileId,
  versionId,
}: UseFileVersionDetailArgs) => {
  const { accessToken } = useAuthInfoWithThrow()

  return useQuery({
    queryKey: fileQueryKeys.version({ fileId, versionId }),
    queryFn: () => fetchFileVersion(fileId, versionId, accessToken),
    enabled: fileId !== '' && versionId !== '',
  })
}

interface UseFileVersionMutationArgs {
  /** The lists the file sits in, so they show its new size and modified time. */
  pursuitId?: UUID | ''
  orgId?: UUID | ''
}

/**
 * What this browser lets go of once a new version is the file. The downloaded copy is
 * reset rather than invalidated, so a viewer showing it reloads to the current bytes.
 */
export const useForgetHeldCopies = ({
  pursuitId = '',
  orgId = '',
}: UseFileVersionMutationArgs) => {
  const queryClient = useQueryClient()

  return useCallback(
    async (fileId: UUID) => {
      const listKeys = [
        ...(pursuitId ? [pursuitFilesQueryKeys.pursuit({ pursuitId })] : []),
        ...(orgId ? [organizationFilesQueryKeys.organization({ orgId })] : []),
      ]
      await Promise.all([
        queryClient.resetQueries({ queryKey: fileQueryKeys.docx({ fileId }) }),
        queryClient.invalidateQueries({
          queryKey: fileQueryKeys.versions({ fileId }),
        }),
        ...listKeys.map((queryKey) =>
          queryClient.invalidateQueries({ queryKey }),
        ),
      ])
    },
    [queryClient, pursuitId, orgId],
  )
}

const useTakeCurrentVersion = (args: UseFileVersionMutationArgs) => {
  const queryClient = useQueryClient()
  const forgetHeldCopies = useForgetHeldCopies(args)

  return useCallback(
    (file: SweetFile) => {
      queryClient.setQueryData(fileQueryKeys.file({ fileId: file.id }), file)
      return forgetHeldCopies(file.id)
    },
    [queryClient, forgetHeldCopies],
  )
}

/**
 * Saves the editor's edits as a version on demand: the Save version button and the
 * history opening. Errors are the caller's to show or pass over.
 */
export const useCheckpointWorkingCopy = (
  args: UseFileVersionMutationArgs = {},
) => {
  const { accessToken } = useAuthInfoWithThrow()
  const queryClient = useQueryClient()
  const forgetHeldCopies = useForgetHeldCopies(args)

  return useMutation<WorkingCopyCheckpoint | null, Error, UUID>({
    mutationFn: (fileId) => checkpointWorkingCopy(fileId, accessToken),
    onSuccess: (checkpoint, fileId) => {
      if (checkpoint?.outcome !== 'saved') {
        return
      }
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: fileQueryKeys.file({ fileId }),
        }),
        forgetHeldCopies(fileId),
      ])
    },
  })
}

interface RestoreFileVersionArgs {
  fileId: UUID
  versionId: UUID
}

/** Makes an earlier version the file again, as a new version. Nothing leaves the history. */
export const useRestoreFileVersion = (
  args: UseFileVersionMutationArgs = {},
) => {
  const { accessToken } = useAuthInfoWithThrow()
  const { handleError } = useHandleError()
  const takeCurrentVersion = useTakeCurrentVersion(args)

  return useMutation<SweetFile, Error, RestoreFileVersionArgs>({
    mutationFn: ({ fileId, versionId }) =>
      restoreFileVersion(fileId, versionId, accessToken),
    onSuccess: takeCurrentVersion,
    onError: (error) => handleError(error),
  })
}

interface UploadFileVersionArgs {
  fileId: UUID
  file: Blob
}

/** Uploads a document over a stored file as its next version: presign, PUT to S3, confirm. */
export const useUploadFileVersion = (args: UseFileVersionMutationArgs = {}) => {
  const { accessToken } = useAuthInfoWithThrow()
  const { handleError } = useHandleError()
  const takeCurrentVersion = useTakeCurrentVersion(args)

  return useMutation<SweetFile, Error, UploadFileVersionArgs>({
    mutationFn: async ({ fileId, file }) => {
      const contentSha256 = (await computeSha256Hex(file)) ?? null
      const upload = await retryWithBackoff(() =>
        fetchVersionUploadUrl(fileId, file.size, contentSha256, accessToken),
      )
      await retryWithBackoff(() => putToS3(upload, file))
      return await retryWithBackoff(() =>
        confirmFileVersion(fileId, upload.s3_key, accessToken),
      )
    },
    onSuccess: takeCurrentVersion,
    onError: (error) => handleError(error),
  })
}
